// Copyright 2022, Imprivata, Inc.  All rights reserved.
import { Flex, Typography } from 'antd';
import DemographicsCard from '../../components/demographics/DemographicsCard';
import {
  Button,
  HexagonSpinner,
  Snackbar,
} from '@imprivata-cloud/patient-access-ui-components';
import Photo from '../../components/photo/Photo';
import { useMemo } from 'react';
import { ErrorComponent } from '../../components/error/ErrorComponent';
import { useEnrollPageState } from './states/useEnrollPageState';
import { navigateWithContext } from '@utils/NavigationHelper';
import { useTranslation } from 'react-i18next';
const EnrollPage = () => {
  const { state, actions } = useEnrollPageState();
  const { t } = useTranslation();
  // Memoize loading state for clarity
  const isLoading = useMemo(
    () =>
      (!state.error?.message &&
        (state.isEnrollmentPending ||
          state.isInquiryLoading ||
          state.isGrantStatusLoading ||
          state.isPatientLoading)) ||
      // Also show loading if we have errors but are still waiting for grant status
      ((state.inquiryError || state.isInquiryLoading || state.createEnrollmentError) && state.isGrantStatusLoading),
    [
      state.error?.message,
      state.isEnrollmentPending,
      state.isInquiryLoading,
      state.isGrantStatusLoading,
      state.isPatientLoading,
      state.inquiryError,
      state.createEnrollmentError,
    ],
  );
  if (state.continueOnOriginalDevice) {
    return <ErrorComponent cause="inquiry-session-mismatch" />;
  }
  if (state.showPatientAlreadyEnrolledError) {
    return <ErrorComponent cause="patient-enrolled" />;
  }
  if (state.showMultipleRecordsFoundError) {
    return <ErrorComponent cause="multiple-records-found" />;
  }

  if (isLoading) {
    return (
      <>
        <Typography.Title
          level={2}
          className="text-center"
          data-testid="enroll-loading-title"
        >
          {t('enrollPage.were-processing-your-information')}
        </Typography.Title>
        <HexagonSpinner data-testid="loading-spinner" />
      </>
    );
  }

  if (state.error?.message === "Invalid request" && state.inquiryError) {
    return <ErrorComponent cause="link-expired" />;
  }

  if (state.grantStatus?.pendingHandoff && !state.error?.message) {
    return (
      <>
        <Snackbar
          variant="info"
          title={t('enrollPage.identity-verification-completed')}
          className="w-100"
          data-testid="enroll-handoff-alert"
        />
        <Typography.Paragraph className="text-center mt-16" data-testid="enroll-handoff-instructions">
          {t('enrollPage.you-can-close-this-window-and-return-to-your-original-device')}
        </Typography.Paragraph>
      </>
    );
  }
  const getPrimaryButtonLabel = () =>
    state.grantStatus?.pendingAuthorization ? t('enrollPage.continue') : undefined;
  const handlePrimaryClick = () => {
    if (state.enrollmentSuccess) {
      navigateWithContext(actions.navigate, '/consent');
    } else {
      actions.postGrant();
    }
  };
  return (
    <Flex align="center" gap={15} vertical className='mt-1 w-100'>
      {state.showPhoto && <Photo image={state.photo ?? undefined} />}
      {!state.error?.message && (
        <DemographicsCard
          givenName={state.patient?.givenName}
          surname={state.patient?.surname}
          dateOfBirth={state.patient?.dateOfBirth}
          mrn={state.patient?.mrn}
          isLoading={state.isPatientLoading}
        />
      )}
      {state.error?.message && (
        <Flex align="center" gap={16} vertical>
          <Snackbar
            variant={state.error.type || 'error'}
            title={state.error?.message ?? ''}
            className="w-100"
          />
          <Typography.Text className="text-center" style={{ whiteSpace: 'pre-line' }}>
            {state.error?.instructions}
          </Typography.Text>
        </Flex>
      )}
      {state.enrollmentSuccess && (
        <>
          <Snackbar
            variant="success"
            title={t('enrollPage.successful-enrollment')}
            className="w-100"
            data-testid="enroll-success-alert"
          />
          {!state.grantStatus?.pendingAuthorization && (
            <Typography.Text className="text-center" data-testid="enroll-close-message">{t('enrollPage.you-may-close-this-window')}</Typography.Text>
          )}
        </>
      )}
      {state.grantStatus?.pendingAuthorization && !state.grantStatus?.pendingHandoff && (
        <Button
          variant="primary"
          label={getPrimaryButtonLabel()}
          data-testid="enroll-continue-btn"
          onClick={handlePrimaryClick}
          className="w-100 max-width-button"
          autoFocus
        />
      )}
    </Flex>
  );
};
export default EnrollPage;
